// noinspection ES6UnusedImports
import React, {Fragment, useEffect, useRef, useState} from 'react';
import Icon from '@bonprix/pattern-library/templates/icon';
import LandmarkAdapter from '@core/scripts/components/landmark-adapter';
import fetchWithCSRF from '@core/scripts/helper/fetch-with-csrf';
import meta from '@core/scripts/helper/meta';
import Breakpoint from '@core/scripts/helper/breakpoint.js';
import {Link, Typography} from '@bonprix/design-system';
import {Button, Dialog, BottomSheet} from '@bonprix/design-system';

const MOBILE_DEVICE_BREAKPOINTS = ['start', 'small'];
const DESKTOP_DEVICE_BREAKPOINTS = ['medium', 'large', 'xlarge', 'max'];

export default function Feedback({
    reviewId,
    texts,
    submitted,
    loggedIn,
    loginLink,
    complained,
    positiveFeedbackCount,
    negativeFeedbackCount,
}) {
    const [currentSubmitted, setCurrentSubmitted] = useState(submitted);
    const [currentPositiveFeedbackCount, setCurrentPositiveFeedbackCount] = useState(positiveFeedbackCount);
    const [currentNegativeFeedbackCount, setCurrentNegativeFeedbackCount] = useState(negativeFeedbackCount);
    const [markingInProgress, setMarkingInProgress] = useState(false);
    const [open, setOpen] = useState(false);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [reported, setReported] = useState(complained);

    const reportContainerRef = useRef();

    useEffect(() => {
        if (open) {
            reportContainerRef.current?.addEventListener('transitionend', setOverFlowVisible);
        }
    }, [open]);

    useEffect(() => {
        setCurrentPositiveFeedbackCount(positiveFeedbackCount);
        setCurrentNegativeFeedbackCount(negativeFeedbackCount);
    }, [positiveFeedbackCount, negativeFeedbackCount]);

    const onOpenReportTooltip = () => {
        setIsDialogOpen(true);
    };

    const onCloseReportTooltip = () => {
        reportContainerRef.current?.addEventListener('transitionend', setActiveFalse);
        setIsDialogOpen(false);
    };

    const onSubmitReport = () => {
        reportReview();
        LandmarkAdapter.sendClickLandmark('adp_reviews_report_send');
    };

    const setOverFlowVisible = () => {
        reportContainerRef.current?.removeEventListener('transitionend', setOverFlowVisible);
    };

    const setActiveFalse = () => {
        reportContainerRef.current?.removeEventListener('transitionend', setActiveFalse);
        setOpen(false);
    };

    const reportReview = () => {
        fetchWithCSRF(`${meta.value('bonprix:baseUrl')}ajax/reviews/complain/${reviewId}/`, {
            method: 'POST',
        })
            .then((response) => {
                if (response.ok) {
                    handleReport(reviewId);
                    setReported(true);
                } else {
                    const error = new Error('Review report failed');
                    error.data = {
                        error: response.body,
                        origMessage: reviewId,
                    };
                    throw error;
                }
            })
            .catch((err) => {
                console.error(err);
            });
    };

    const markReview = (positive) => {
        fetchWithCSRF(`${meta.value('bonprix:baseUrl')}ajax/reviews/feedback/`, {
            method: 'POST',
            headers: {
                Accept: 'application/json, text/plain, */*',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({positive, reviewId}),
        })
            .then((response) => {
                if (response.ok) {
                    return response.json();
                } else {
                    const error = new Error('Review feedback failed');
                    error.data = {
                        error: response.body,
                        origMessage: reviewId,
                    };
                    onMarkReviewFailed();
                    throw error;
                }
            })
            .then((data) => {
                const {positiveFeedbackCount, negativeFeedbackCount} = data;
                onMarkReviewSuccessful(positiveFeedbackCount, negativeFeedbackCount);
            })
            .catch((err) => {
                onMarkReviewFailed();
                console.error(err);
            });
    };

    const markHelpful = () => {
        if (markingInProgress) {
            return;
        }
        setMarkingInProgress(true);
        LandmarkAdapter.sendClickLandmark('adp_reviews_helpful');
        markReview(true);
    };

    const markNotHelpful = () => {
        if (markingInProgress) {
            return;
        }
        setMarkingInProgress(true);
        LandmarkAdapter.sendClickLandmark('adp_reviews_helpful_not');
        markReview(false);
    };

    const onMarkReviewSuccessful = (positiveCount, negativeCount) => {
        handleCounts(positiveCount, negativeCount);
        setMarkingInProgress(false);
    };

    const onMarkReviewFailed = () => {
        setMarkingInProgress(false);
    };

    const handleCounts = (positiveCount, negativeCount) => {
        setCurrentPositiveFeedbackCount(positiveCount);
        setCurrentNegativeFeedbackCount(negativeCount);
    };

    const handleReport = (id) => {
        if (reviewId === id) {
            onCloseReportTooltip();
            setCurrentSubmitted(true);
        }
    };

    const toggleReportTooltip = (e) => {
        e.preventDefault();
        onReportLinkClicked();
        if (isDialogOpen) {
            onCloseReportTooltip();
        } else {
            onOpenReportTooltip();
        }
    };

    const onReportLinkClicked = () => {
        LandmarkAdapter.sendClickLandmark('adp_reviews_report');
    };

    const FeedbackLink = () => {
        return (
            <Link href={loginLink} onClick={loggedIn ? toggleReportTooltip : onReportLinkClicked}>
                {texts.mobil_ada_review_report}
            </Link>
        );
    };

    const showBottomSheet = loggedIn && Breakpoint.keyIsIn(MOBILE_DEVICE_BREAKPOINTS);
    const showDialog = loggedIn && Breakpoint.keyIsIn(DESKTOP_DEVICE_BREAKPOINTS);
    const showSubmitMessage = currentSubmitted || complained;

    return (
        <>
            {/* OPENS IN BOTTOM SHEET FOR MOBILE */}
            {showBottomSheet && (
                <BottomSheet
                    isOpen={isDialogOpen}
                    onClose={() => setIsDialogOpen(false)}
                    onSnap={() => setIsDialogOpen(false)}
                >
                    <div className="review__report-bottom-sheet">
                        <div className="review__report-headline">
                            <Typography variant="h5">{texts.bff_review_report_question}</Typography>
                        </div>
                        <div className="review__report-copy">
                            <Typography variant="body1">{texts.bff_review_report_hint}</Typography>
                        </div>
                        <div className="review__report-buttons -small">
                            <Button onClick={onSubmitReport} variant="primary">{texts.mobil_ada_review_report}</Button>
                            <Button onClick={onCloseReportTooltip} variant="secondary">{texts.review_complain_layer_abort}</Button>
                        </div>
                    </div>
                </BottomSheet>
            )}

            {/* OPENS IN DIALOG FOR DESKTOP */}
            {showDialog && (
                <Dialog isOpen={isDialogOpen} onClose={() => setIsDialogOpen(false)}>
                    <Dialog.Title>
                        {texts.bff_review_report_question}
                    </Dialog.Title>
                    <Dialog.Content>
                        <div className="flex w-full justify-center items-center">
                            <Typography variant="body1">{texts.bff_review_report_hint}</Typography>
                        </div>
                    </Dialog.Content>
                    <Dialog.PrimaryButton>
                        <Button onClick={onSubmitReport} variant="primary">{texts.mobil_ada_review_report}</Button>
                    </Dialog.PrimaryButton>
                    <Dialog.SecondaryButton>
                        <Button onClick={onCloseReportTooltip} variant="secondary">{texts.review_complain_layer_abort}</Button>
                    </Dialog.SecondaryButton>
                </Dialog>
            )}

            {showSubmitMessage && (
                <div className="review__report-submitted-message">{texts.ada_review_gemeldet_danke}</div>
            )}

            {!reported && (
                <div className="review__feedback-links">
                    <div className="review__helpful">
                        <div className="review__helpful-text"><Typography
                            variant="body1">{texts.review_feedback_helpful}</Typography></div>
                        <div className="review__helpful-action" onClick={markHelpful}>
                            <Icon name="thumb_up" className="review__helpful-icon"/>
                            <Typography variant="body2">{currentPositiveFeedbackCount}</Typography>
                        </div>
                        <div className="review__helpful-action" onClick={markNotHelpful}>
                            <Icon name="thumb_down" className="review__helpful-icon" />
                            <Typography variant="body2">{currentNegativeFeedbackCount}</Typography>
                        </div>
                    </div>
                    <FeedbackLink
                        loggedIn={loggedIn}
                        loginLink={loginLink}
                        texts={texts}
                        onClick={toggleReportTooltip}
                        onReportLinkClicked={onReportLinkClicked}
                    />
                </div>
            )}
        </>
    );
}
